import CollectorCard from '../passport/CollectorCard';
import { sourceCard } from '../passport/sourceCards';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiService from '../../services/ApiService';
import { Button } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';
import { PageLoader } from '../ui/PageLoader';
import FeatureTip from '../onboarding/FeatureTip';
import PageShell from '../ui/PageShell';
import { DirectoryBrowseLayout, FilterSection } from '../ui/DirectoryBrowseLayout';
import type { Club, PaginationMeta } from '../../types/discoveryBadges';
import { useAuth } from '../../contexts/AuthContext';

const CLUB_TYPES: [string, string][] = [
  ['marque', 'Marque'],
  ['regional', 'Regional'],
  ['track', 'Track'],
  ['show', 'Show'],
  ['lifestyle', 'Lifestyle'],
  ['other', 'Other'],
];

const VERIFICATIONS: [string, string][] = [
  ['verified', 'Verified'],
  ['partner', 'Partner'],
  ['unverified', 'Listed'],
];

type ClubFilters = {
  search?: string;
  region?: string;
  club_type?: string;
  verification?: string;
  page: number;
};

function filtersFromParams(params: URLSearchParams): ClubFilters {
  return {
    search: params.get('q') || undefined,
    region: params.get('region') || undefined,
    club_type: params.get('type') || undefined,
    verification: params.get('verification') || undefined,
    page: Math.max(1, Number(params.get('page') || 1)),
  };
}

function filtersToParams(filters: ClubFilters): URLSearchParams {
  const next = new URLSearchParams();
  if (filters.search) next.set('q', filters.search);
  if (filters.region) next.set('region', filters.region);
  if (filters.club_type) next.set('type', filters.club_type);
  if (filters.verification) next.set('verification', filters.verification);
  if (filters.page > 1) next.set('page', String(filters.page));
  return next;
}

const ClubsDirectoryPage: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => filtersFromParams(params), [params]);
  const [draft, setDraft] = useState<ClubFilters>(filters);
  const [searchInput, setSearchInput] = useState(filters.search || '');
  const [clubs, setClubs] = useState<Club[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta>({
    page: 1,
    limit: 30,
    total_count: 0,
    total_pages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isAuthenticated, isOrganizer } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setDraft(filters);
    setSearchInput(filters.search || '');
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.listClubs({
        search: filters.search || '',
        region: filters.region || '',
        club_type: filters.club_type || '',
        verification: filters.verification || '',
        page: filters.page,
        limit: 30,
      });
      setClubs(result.data);
      setPagination(
        result.pagination || {
          page: filters.page,
          limit: 30,
          total_count: result.data.length,
          total_pages: 1,
        }
      );
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load clubs');
      setClubs([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    void load();
  }, [load]);

  const applyDraft = () => {
    setParams(filtersToParams({ ...draft, search: searchInput.trim() || undefined, page: 1 }), {
      replace: true,
    });
    setMobileOpen(false);
  };

  const clearAll = () => {
    setParams(new URLSearchParams(), { replace: true });
    setSearchInput('');
    setDraft({ page: 1 });
  };

  const activeCount = [filters.region, filters.club_type, filters.verification].filter(
    Boolean
  ).length;
  const hasActive = activeCount > 0 || Boolean(filters.search);

  return (
    <PageShell
      width="full"
      contentClassName="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8 flex-1 w-full"
    >
      <DirectoryBrowseLayout
        title="Clubs"
        description="Find your people. Marque clubs and communities on Bilt:Spec."
        mobileTriggerLabel="Clubs"
        sections={[
          { id: 'clubs-results', label: 'Results', description: 'Communities on Bilt:Spec' },
        ]}
        activeFilterCount={activeCount}
        mobileOpen={mobileOpen}
        onMobileOpenChange={setMobileOpen}
        groups={[
          {
            id: 'filters',
            label: 'Filters',
            children: (
              <div className="space-y-4">
                <FilterSection id="type" label="Club type">
                  <div className="flex flex-wrap gap-2">
                    {CLUB_TYPES.map(([val, label]) => (
                      <button
                        key={val}
                        type="button"
                        className={draft.club_type === val ? 'filter-chip-active' : 'filter-chip'}
                        onClick={() =>
                          setDraft({
                            ...draft,
                            club_type: draft.club_type === val ? undefined : val,
                          })
                        }
                        aria-pressed={draft.club_type === val}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </FilterSection>
                <FilterSection id="verification" label="Trust">
                  <div className="flex flex-wrap gap-2">
                    {VERIFICATIONS.map(([val, label]) => (
                      <button
                        key={val}
                        type="button"
                        className={
                          draft.verification === val ? 'filter-chip-active' : 'filter-chip'
                        }
                        onClick={() =>
                          setDraft({
                            ...draft,
                            verification: draft.verification === val ? undefined : val,
                          })
                        }
                        aria-pressed={draft.verification === val}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </FilterSection>
                <FilterSection id="region" label="Region">
                  <label className="block text-sm text-gray-300">
                    Region
                    <input
                      className="mt-1 w-full input min-h-[40px]"
                      value={draft.region || ''}
                      onChange={(e) => setDraft({ ...draft, region: e.target.value || undefined })}
                      placeholder="City, state, or area"
                    />
                  </label>
                </FilterSection>
              </div>
            ),
            footer: (
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  className="flex-1"
                  onClick={() => setDraft({ page: 1 })}
                >
                  Reset
                </Button>
                <Button type="button" variant="primary" className="flex-[2]" onClick={applyDraft}>
                  Apply
                </Button>
              </div>
            ),
          },
        ]}
      >
        <div id="clubs-results">
          <FeatureTip tipId="clubs_directory" className="mb-4">
            Join communities connected to the cars, events, and people you care about.
          </FeatureTip>
          {isAuthenticated && !isOrganizer ? (
            <p className="text-sm text-gray-500 mb-4">
              Creating clubs requires organizer access. Ask an admin for an organizer invite or
              capability grant.
            </p>
          ) : null}
          <form
            className="flex flex-col sm:flex-row gap-3 mb-4"
            onSubmit={(e) => {
              e.preventDefault();
              setParams(
                filtersToParams({ ...filters, search: searchInput.trim() || undefined, page: 1 }),
                { replace: true }
              );
            }}
            role="search"
          >
            <input
              className="input flex-1 min-h-[44px]"
              placeholder="Search clubs or regions"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label="Search clubs"
            />
            <Button type="submit" variant="primary">
              Search
            </Button>
          </form>

          <div className="flex flex-wrap items-center gap-2 mb-4" aria-label="Active filters">
            {filters.club_type ? (
              <button
                type="button"
                className="filter-chip"
                onClick={() =>
                  setParams(filtersToParams({ ...filters, club_type: undefined, page: 1 }), {
                    replace: true,
                  })
                }
              >
                Type: {CLUB_TYPES.find(([v]) => v === filters.club_type)?.[1] || filters.club_type}{' '}
                ×
              </button>
            ) : null}
            {filters.verification ? (
              <button
                type="button"
                className="filter-chip"
                onClick={() =>
                  setParams(filtersToParams({ ...filters, verification: undefined, page: 1 }), {
                    replace: true,
                  })
                }
              >
                {VERIFICATIONS.find(([v]) => v === filters.verification)?.[1] ||
                  filters.verification}{' '}
                ×
              </button>
            ) : null}
            {filters.region ? (
              <button
                type="button"
                className="filter-chip"
                onClick={() =>
                  setParams(filtersToParams({ ...filters, region: undefined, page: 1 }), {
                    replace: true,
                  })
                }
              >
                Region: {filters.region} ×
              </button>
            ) : null}
            {hasActive ? (
              <button
                type="button"
                className="text-sm text-orange-400 hover:text-orange-300 cursor-pointer"
                onClick={clearAll}
              >
                Clear all
              </button>
            ) : null}
            <span className="text-sm text-gray-500 ml-auto" aria-live="polite">
              {pagination.total_count} clubs
            </span>
          </div>

          {loading ? <PageLoader message="Loading clubs" /> : null}
          {error ? (
            <EmptyState
              title="Couldn’t load clubs"
              description={error}
              action={
                <Button
                  type="button"
                  variant="primary"
                  className="min-h-[44px]"
                  onClick={() => void load()}
                >
                  Try again
                </Button>
              }
            />
          ) : null}
          {!loading && !error && clubs.length === 0 ? (
            <EmptyState
              imageSrc={hasActive ? undefined : '/images/brand/empty-clubs-overlook.jpg'}
              imageAlt=""
              title={hasActive ? 'No clubs match' : 'No clubs yet'}
              description={
                hasActive
                  ? 'Try a different name, type, or region.'
                  : 'Be the first to list a club.'
              }
              action={
                hasActive ? (
                  <Button type="button" variant="secondary" onClick={clearAll}>
                    Clear filters
                  </Button>
                ) : isAuthenticated && isOrganizer ? (
                  <Button
                    type="button"
                    variant="primary"
                    className="min-h-[44px]"
                    onClick={() => navigate('/clubs/new')}
                  >
                    Create club
                  </Button>
                ) : undefined
              }
            />
          ) : !loading && !error ? (
            <>
              <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6 list-none">
                {clubs.map((club) => (
                  <li key={club.id}>
                    <CollectorCard
                      href={`/clubs/${club.slug}`}
                      card={sourceCard({
                        kind: 'club',
                        id: club.id,
                        title: club.name,
                        logo: apiService.getFileUrl(club.logo_path || '') || club.logo_path,
                        subtitle: club.region,
                        description: club.description,
                        slug: club.slug,
                        facts: {
                          region: club.region || null,
                          focus: club.club_type || null,
                          member_count: club.member_count ?? null,
                          verification_status: club.is_verified ? 'verified' : null,
                        },
                      })}
                      fullRecordExtension={
                        <>
                          {club.is_verified && (
                            <span className="text-xs text-orange-300">Verified</span>
                          )}
                          {club.description && (
                            <p className="text-sm text-gray-400 line-clamp-2">{club.description}</p>
                          )}
                          {club.member_count != null && (
                            <p className="text-sm text-gray-400">{club.member_count} members</p>
                          )}
                        </>
                      }
                    />
                  </li>
                ))}
              </ul>
              {pagination.total_pages > 1 ? (
                <div className="flex justify-center gap-3 mt-8">
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={pagination.page <= 1}
                    onClick={() =>
                      setParams(filtersToParams({ ...filters, page: pagination.page - 1 }), {
                        replace: true,
                      })
                    }
                  >
                    Previous
                  </Button>
                  <span className="text-gray-400 self-center text-sm">
                    Page {pagination.page} of {pagination.total_pages}
                  </span>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={pagination.page >= pagination.total_pages}
                    onClick={() =>
                      setParams(filtersToParams({ ...filters, page: pagination.page + 1 }), {
                        replace: true,
                      })
                    }
                  >
                    Next
                  </Button>
                </div>
              ) : null}
              {isAuthenticated && isOrganizer ? (
                <div className="mt-10 pt-6 border-t border-white/[0.06] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <p className="text-sm text-gray-400 max-w-md">
                    Start a club for your marque, region, or scene.
                  </p>
                  <Button type="button" variant="primary" onClick={() => navigate('/clubs/new')}>
                    Create club
                  </Button>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </DirectoryBrowseLayout>
    </PageShell>
  );
};

export default ClubsDirectoryPage;
